import {
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserService } from '@app/services/user/user.service';
import { OrderBatchCategory, OrderBatchDTO, PdqUser } from '@pdqdev/pdq-shared/dist/entities';
import {
  createLocationFilterOptions,
  createOwnerFilterOptions,
  createCarrierFilterOptions,
} from '@utils/batch-filter.utils';
import { Store } from '@ngrx/store';
import * as fromCarriers from '@app/pages/delivery-service/store/reducers/index';
import { ShopService } from '@app/services/shops/shop.service';
import {
  BatchBulkAction,
  BatchBulkActionType,
  BatchesState,
  BatchFilters,
  BatchFilterOptions,
  BatchTableColumn,
} from '@app/sharedModule/models/paginated-batch-table.model';
import { BatchRowDTO } from '@app/sharedModule/components/paginated-batch-table/components/batch-table-row/batch-table-row.component';
import { PaginatedBatchTableComponent } from '@app/sharedModule/components/paginated-batch-table/paginated-batch-table.component';
import { FulfillService } from '../../fulfill.service';
import { PanelState } from '../../utils/models';

@Component({
  selector: 'fulfill-batch-list-table-v2',
  templateUrl: './fulfill-batch-list-table-v2.component.html',
  styleUrls: ['./fulfill-batch-list-table-v2.component.scss'],
  standalone: false,
})
export class FulfillBatchListTableV2Component implements OnDestroy, OnInit {
  @ViewChild(PaginatedBatchTableComponent) paginatedTable: PaginatedBatchTableComponent;

  private destroyRef = inject(DestroyRef);

  @Input() category: OrderBatchCategory;
  @Input() columns: BatchTableColumn[];
  @Input() pdqUsersList: PdqUser[];
  @Input() title: string;
  @Input() allowReorder: boolean = false;

  // State inputs from parent
  @Input() batchesState: BatchesState;
  @Input() totalCount: number = 0;
  @Input() countLoading: boolean = false;

  // Event outputs to parent
  @Output() loadPage = new EventEmitter<{ page: number }>();
  @Output() filterChange = new EventEmitter<BatchFilters>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() batchReorder = new EventEmitter<{
    movedBatch: OrderBatchDTO;
    targetBatch: OrderBatchDTO;
    insertBefore: boolean;
  }>();

  // Filter options
  carriers: BatchFilterOptions;
  locations = computed(() =>
    createLocationFilterOptions(
      this.shopService.shopSignal()?.shopLocations || [],
      this.userService.pdqUserSignal()?.locations,
    ),
  );
  owners: BatchFilterOptions;
  fulfillBatchBulkActions = computed(() => this.shopService.metadataSignal()?.fulfillBatchBulkActions);
  readonly bulkActions: BatchBulkAction[] = [{ type: BatchBulkActionType.PRINT }];

  carriers$ = this.carriersStore.select(fromCarriers.selectCarriers);

  constructor(
    private readonly carriersStore: Store<fromCarriers.State>,
    private userService: UserService,
    private shopService: ShopService,
    private fulfillService: FulfillService,
  ) {}

  ngOnInit(): void {
    this.setOwnerOptions();
    this.setCarriersOptions();
  }

  ngOnDestroy(): void {
    this.carriers = null;
    this.owners = null;
  }

  setOwnerOptions(): void {
    this.userService.users$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((users) => {
      if (!users?.length) {
        return;
      }
      this.owners = createOwnerFilterOptions(users);
    });
  }

  setCarriersOptions(): void {
    this.carriers$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((carriers) => {
      this.carriers = createCarrierFilterOptions(carriers);
    });
  }

  // Handle loadPage event from paginated-batch-table
  onLoadPage(event: { page: number }): void {
    this.loadPage.emit(event);
  }

  // Handle filterChange event from paginated-batch-table
  onFilterChange(filters: BatchFilters): void {
    this.filterChange.emit(filters);
  }

  // Handle pageSizeChange event from paginated-batch-table
  onPageSizeChange(pageSize: number): void {
    this.pageSizeChange.emit(pageSize);
  }

  // Handle row click - set active batch and open panel
  onRowClick(batch: BatchRowDTO): void {
    this.fulfillService.setActiveBatch(batch);
    this.fulfillService.setPanelState(PanelState.BATCH);
  }

  // Get current page from paginated table for cache invalidation logic
  getCurrentPage(): number {
    return this.paginatedTable?.currentPage() ?? 1;
  }

  resetToFirstPage(): void {
    this.paginatedTable?.resetScrollState();
  }

  isBatchSelected(batchId: number): boolean {
    return this.paginatedTable?.selectedBatchIds().has(batchId) ?? false;
  }

  unselectBatch(batchId: number): void {
    this.paginatedTable?.toggleBatchSelection(batchId, false);
  }

  // Handle batch reorder event from paginated-batch-table
  onBatchReorder(event: { movedBatch: OrderBatchDTO; targetBatch: OrderBatchDTO; insertBefore: boolean }): void {
    this.batchReorder.emit(event);
  }
}
